/**
 * MO.STORE — product page layer (classic deferred script, product template only)
 * ---------------------------------------------------------------------------
 * UX only. Variants, stock, cart and checkout stay 100 % native Shopify.
 * The "size required" guard itself is inline in <head>
 * (snippets/mo-size-guard.liquid) so it exists before first paint.
 *
 *  1. Commander     [data-mo-buy-now] → POST /cart/add.js (native cart) with
 *                   the chosen variant + quantity, then Shopify checkout.
 *  2. Recap sync    after Horizon fetches the section for the new variant
 *                   (standard productSelect event from '@shopify/events'),
 *                   the fresh recap and the button state are swapped in.
 *                   Fallback if that module can't load: fetch the section.
 *  3. Size guide    [data-mo-size-guide-open] opens the <dialog>.
 */
(() => {
  /* ---------------------------------------------------------- 1. commander */

  function selectedVariantId(scope) {
    const checked = scope.querySelector('variant-picker input[type="radio"]:checked');
    return (
      checked?.dataset.variantId ||
      scope.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value
    );
  }

  async function buyNow(button) {
    if (button.disabled || button.classList.contains('is-loading')) return;
    const scope = button.closest('.shopify-section') ?? document;
    const errorEl = scope.querySelector('[data-mo-buy-now-error]');
    const id = Number(selectedVariantId(scope));
    const quantity = Math.max(parseInt(scope.querySelector('input[name="quantity"]')?.value, 10) || 1, 1);
    if (!id) return;

    if (errorEl) errorEl.hidden = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(button.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity }] }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || data.message || '');
      }
      window.location.assign(button.dataset.checkoutUrl || '/checkout');
    } catch (error) {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      if (errorEl) {
        errorEl.textContent =
          error instanceof Error && error.message
            ? error.message
            : "Impossible d'ajouter ce pack pour le moment. Réessaie dans un instant.";
        errorEl.hidden = false;
      }
    }
  }

  // runs after the inline guard (registered earlier on window/capture):
  // when no size is chosen the guard has already stopped this click.
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('[data-mo-buy-now]');
      if (!button) return;
      if (window.MoSize?.pending()) return;
      event.preventDefault();
      buyNow(/** @type {HTMLButtonElement} */ (button));
    },
    true
  );

  /* -------------------------------------------------------- 2. recap sync */

  function syncFromHtml(scope, html) {
    const summary = scope.querySelector('[data-mo-summary]');
    const freshSummary = html.querySelector('[data-mo-summary]');
    if (summary && freshSummary) summary.replaceWith(document.importNode(freshSummary, true));

    const buy = scope.querySelector('[data-mo-buy-now]');
    const freshBuy = html.querySelector('[data-mo-buy-now]');
    if (buy && freshBuy) {
      buy.disabled = freshBuy.disabled;
      buy.classList.remove('is-loading');
    }
    const error = scope.querySelector('[data-mo-buy-now-error]');
    if (error) error.hidden = true;

    // quantity × price recap (assets/mo-store-effects.js)
    document.dispatchEvent(new CustomEvent('quantity-selector:update'));
  }

  function sectionsToWatch() {
    return [...new Set([...document.querySelectorAll('[data-mo-summary], [data-mo-buy-now]')]
      .map((el) => el.closest('.shopify-section'))
      .filter(Boolean))];
  }

  function watchWithEvents(StandardEvents) {
    sectionsToWatch().forEach((section) => {
      if (section.dataset.moRecapReady) return;
      section.dataset.moRecapReady = 'true';
      section.addEventListener(StandardEvents.productSelect, (event) => {
        if (!(event.target instanceof Element) || event.target.closest('product-card, quick-add-dialog')) return;
        event.promise
          ?.then(({ detail }) => detail?.html && syncFromHtml(section, detail.html))
          .catch((error) => {
            if (error?.name !== 'AbortError') console.warn('[mo-store] recap sync', error);
          });
      });
    });
  }

  // fallback: fetch the section ourselves after a size change
  function watchWithFetch() {
    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      const picker = input.closest('variant-picker');
      const section = picker?.closest('.shopify-section');
      const variantId = input.dataset.variantId;
      if (!section || !variantId || !section.querySelector('[data-mo-summary]')) return;
      const sectionId = picker.dataset.sectionId;
      const url = `${picker.dataset.productUrl}?variant=${variantId}&section_id=${sectionId}`;
      fetch(url)
        .then((r) => r.text())
        .then((text) => syncFromHtml(section, new DOMParser().parseFromString(text, 'text/html')))
        .catch(() => {});
    });
  }

  let eventsMode = false;
  import('@shopify/events')
    .then(({ StandardEvents }) => {
      if (!StandardEvents?.productSelect) throw new Error('no productSelect');
      eventsMode = true;
      watchWithEvents(StandardEvents);
      document.addEventListener('shopify:section:load', () => watchWithEvents(StandardEvents));
    })
    .catch(() => {
      if (!eventsMode) watchWithFetch();
    });

  /* -------------------------------------------------------- 3. size guide */

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const open = target.closest('[data-mo-size-guide-open]');
    if (open) {
      const dialog = open.parentElement?.querySelector('dialog[data-mo-size-guide]');
      if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
      return;
    }

    const dialog = target.closest('dialog[data-mo-size-guide]');
    if (dialog instanceof HTMLDialogElement && (target.closest('[data-mo-size-guide-close]') || target === dialog)) {
      dialog.close();
    }
  });
})();
